/**
 * Money handling for CPCel.
 *
 * Every monetary value in the system flows through this module. The rules are:
 *
 *  1. No JavaScript floating point arithmetic is ever used for money.
 *  2. Values are stored in PostgreSQL as NUMERIC(14,2) (amounts) or
 *     NUMERIC(18,6) (unit costs, which need sub-paisa precision so that
 *     weighted average cost does not drift).
 *  3. Values cross the wire as strings, never as numbers, so that nothing is
 *     silently coerced into a double along the way.
 *
 * `Money` is a `Decimal` from decimal.js. Prisma's own Decimal is the same
 * library, but we deliberately hand Prisma *strings* on write and re-wrap
 * *strings* on read, so the two copies never have to agree on an internal
 * representation.
 */
import Decimal from 'decimal.js';

// 34 significant digits is far more than any Customer Point will ever need and
// keeps intermediate results (e.g. weighted average cost) exact for our range.
Decimal.set({
  precision: 34,
  rounding: Decimal.ROUND_HALF_UP,
  toExpNeg: -30,
  toExpPos: 30,
});

export type Money = Decimal;

// ---------------------------------------------------------------------------
// Precision policy
//
// Three different precisions are in play and confusing them is how a ledger
// ends up one paisa out:
//
//   CALCULATION — 34 significant digits, set on the Decimal constructor above.
//                 Intermediate results are never rounded.
//   STORAGE     — what a column holds: 2dp for amounts, 6dp for unit costs.
//   DISPLAY     — what a person reads. Never feeds back into arithmetic.
//
// Rounding happens once, on the way into storage, and is half-up throughout —
// the convention a Bangladeshi retailer expects, and the one a hand-written
// cash book would use.
// ---------------------------------------------------------------------------

/** Number of decimal places used for amounts (BDT has 2 — paisa). */
export const AMOUNT_SCALE = 2;
/** Number of decimal places used for unit costs held in inventory. */
export const COST_SCALE = 6;
/** Decimal places shown to a user. Matches the amount scale by design. */
export const DISPLAY_SCALE = 2;
/** Significant digits kept for intermediate results. */
export const CALCULATION_PRECISION = 34;

export type MoneyInput = Money | string | number | null | undefined | { toString(): string };

/**
 * Coerce anything that came out of the database, a form, or JSON into a Money.
 * `null`/`undefined`/empty string become zero, which is what every caller in
 * this codebase wants (an absent amount is an amount of nothing).
 */
export function money(value: MoneyInput): Money {
  if (value === null || value === undefined) return new Decimal(0);
  if (value instanceof Decimal) return value;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      throw new TypeError(`Cannot build Money from non-finite number: ${value}`);
    }
    // Route through the string form so that 0.1 + 0.2 style artefacts from the
    // caller's own arithmetic cannot leak in.
    return new Decimal(value.toString());
  }
  const text = String(value).trim();
  if (text === '') return new Decimal(0);
  const normalised = text.replace(/[,\s৳]/g, '');
  if (!/^-?\d*\.?\d+$/.test(normalised)) {
    throw new TypeError(`Cannot build Money from: ${JSON.stringify(text)}`);
  }
  return new Decimal(normalised);
}

/** Money constant zero. */
export const ZERO: Money = new Decimal(0);

export function add(...values: MoneyInput[]): Money {
  return values.reduce<Money>((acc, v) => acc.plus(money(v)), new Decimal(0));
}

export function sub(a: MoneyInput, b: MoneyInput): Money {
  return money(a).minus(money(b));
}

export function mul(a: MoneyInput, b: MoneyInput | number): Money {
  return money(a).times(money(b as MoneyInput));
}

export function div(a: MoneyInput, b: MoneyInput | number): Money {
  const divisor = money(b as MoneyInput);
  if (divisor.isZero()) return new Decimal(0);
  return money(a).dividedBy(divisor);
}

export function sum(values: readonly MoneyInput[]): Money {
  return values.reduce<Money>((acc, v) => acc.plus(money(v)), new Decimal(0));
}

export function sumBy<T>(items: readonly T[], pick: (item: T) => MoneyInput): Money {
  return items.reduce<Money>((acc, item) => acc.plus(money(pick(item))), new Decimal(0));
}

export function neg(a: MoneyInput): Money {
  return money(a).negated();
}

export function abs(a: MoneyInput): Money {
  return money(a).abs();
}

/** Round to amount scale (2dp) using half-up, the convention used in BD retail. */
export function round(a: MoneyInput, scale = AMOUNT_SCALE): Money {
  return money(a).toDecimalPlaces(scale, Decimal.ROUND_HALF_UP);
}

/** Round to cost scale (6dp) — used for weighted average unit cost. */
export function roundCost(a: MoneyInput): Money {
  return money(a).toDecimalPlaces(COST_SCALE, Decimal.ROUND_HALF_UP);
}

// ---------------------------------------------------------------------------
// Rounding rules for specific kinds of money
//
// Each of these is a named policy rather than a bare `round` call, so that
// changing how CPCel rounds a discount is one edit in one place instead of a
// search for every `.toDecimalPlaces` in the codebase. They are all half-up at
// 2dp today; they exist separately because they are separate decisions.
// ---------------------------------------------------------------------------

/**
 * A discount, whether typed in as an amount or derived from a percentage.
 * Rounded to paisa so that `total = subtotal − discount` holds exactly and the
 * printed receipt adds up.
 */
export function roundDiscount(a: MoneyInput): Money {
  return round(a, AMOUNT_SCALE);
}

/**
 * Commission — GP incentive and anything else earned as a rate on volume.
 * Rounded at the point it is recorded, never re-derived at display time, so the
 * receivable and the receipt can be compared paisa for paisa.
 */
export function roundCommission(a: MoneyInput): Money {
  return round(a, AMOUNT_SCALE);
}

/**
 * A refund. Rounded the same way as the original sale line so that refunding
 * every line of a sale returns exactly what was charged, with nothing stranded.
 */
export function roundRefund(a: MoneyInput): Money {
  return round(a, AMOUNT_SCALE);
}

/**
 * A percentage-of-amount calculation: `amount × percent / 100`.
 * The multiplication runs at full precision and is rounded once, at the end.
 */
export function percentageOf(amount: MoneyInput, percent: MoneyInput): Money {
  return round(money(amount).times(money(percent)).dividedBy(100), AMOUNT_SCALE);
}

/**
 * Split an amount across `parts` shares whose total is exactly the original.
 *
 * Used where an invoice-level figure has to be pushed down onto lines — an
 * order discount spread over its items, say. The remainder from rounding is
 * handed to the earliest shares one paisa at a time rather than being dropped,
 * which is why the returned values always sum back to `amount`.
 */
export function allocate(amount: MoneyInput, weights: readonly MoneyInput[]): Money[] {
  const total = round(amount, AMOUNT_SCALE);
  const parsed = weights.map(money);
  const weightTotal = parsed.reduce<Money>((acc, w) => acc.plus(w), new Decimal(0));

  if (parsed.length === 0) return [];
  if (weightTotal.isZero()) {
    // Nothing to weigh by: give the whole amount to the first share.
    return parsed.map((_, index) => (index === 0 ? total : new Decimal(0)));
  }

  const shares = parsed.map((w) => round(total.times(w).dividedBy(weightTotal), AMOUNT_SCALE));
  const allocated = shares.reduce<Money>((acc, s) => acc.plus(s), new Decimal(0));
  let remainder = total.minus(allocated);

  const step = new Decimal(1).dividedBy(new Decimal(10).pow(AMOUNT_SCALE));
  const direction = remainder.isNegative() ? step.negated() : step;
  let index = 0;
  while (!remainder.isZero() && index < shares.length) {
    shares[index] = (shares[index] as Money).plus(direction);
    remainder = remainder.minus(direction);
    index += 1;
  }

  return shares;
}

export function isZero(a: MoneyInput): boolean {
  return money(a).isZero();
}

export function isNegative(a: MoneyInput): boolean {
  return money(a).isNegative() && !money(a).isZero();
}

export function isPositive(a: MoneyInput): boolean {
  return money(a).greaterThan(0);
}

export function cmp(a: MoneyInput, b: MoneyInput): -1 | 0 | 1 {
  return money(a).comparedTo(money(b)) as -1 | 0 | 1;
}

export function eq(a: MoneyInput, b: MoneyInput): boolean {
  return money(a).equals(money(b));
}

export function gt(a: MoneyInput, b: MoneyInput): boolean {
  return money(a).greaterThan(money(b));
}

export function gte(a: MoneyInput, b: MoneyInput): boolean {
  return money(a).greaterThanOrEqualTo(money(b));
}

export function lt(a: MoneyInput, b: MoneyInput): boolean {
  return money(a).lessThan(money(b));
}

export function lte(a: MoneyInput, b: MoneyInput): boolean {
  return money(a).lessThanOrEqualTo(money(b));
}

export function maxMoney(...values: MoneyInput[]): Money {
  return values.map(money).reduce((a, b) => (a.greaterThan(b) ? a : b), new Decimal(0));
}

export function minMoney(...values: MoneyInput[]): Money {
  const list = values.map(money);
  if (list.length === 0) return new Decimal(0);
  return list.reduce((a, b) => (a.lessThan(b) ? a : b));
}

/** Clamp a value so it is never below zero. */
export function atLeastZero(a: MoneyInput): Money {
  const v = money(a);
  return v.isNegative() ? new Decimal(0) : v;
}

/**
 * Percentage of `part` relative to `whole`, rounded to 2dp.
 * Returns 0 when `whole` is zero rather than NaN/Infinity.
 */
export function percentOf(part: MoneyInput, whole: MoneyInput): Money {
  const w = money(whole);
  if (w.isZero()) return new Decimal(0);
  return round(money(part).dividedBy(w).times(100), 2);
}

/**
 * Profit margin: profit as a percentage of the selling price.
 * ৳1000 sale on ৳800 cost => 20.00
 */
export function marginPercent(sellingPrice: MoneyInput, cost: MoneyInput): Money {
  const price = money(sellingPrice);
  if (price.isZero()) return new Decimal(0);
  return round(price.minus(money(cost)).dividedBy(price).times(100), 2);
}

/** The value written to a NUMERIC(14,2) column. Always a fixed-scale string. */
export function toAmountString(a: MoneyInput): string {
  return round(a, AMOUNT_SCALE).toFixed(AMOUNT_SCALE);
}

/** The value written to a NUMERIC(18,6) column. */
export function toCostString(a: MoneyInput): string {
  return roundCost(a).toFixed(COST_SCALE);
}

/**
 * Serialise for transport to a client component / JSON payload.
 * Client code must treat this as an opaque decimal string.
 */
export function serialize(a: MoneyInput): string {
  return toAmountString(a);
}

/**
 * Escape hatch for the few places that genuinely need a JS number — chart
 * plotting and nothing else. Never use the result for further arithmetic that
 * ends up persisted.
 */
export function toChartNumber(a: MoneyInput): number {
  return money(a).toDecimalPlaces(AMOUNT_SCALE, Decimal.ROUND_HALF_UP).toNumber();
}

export { Decimal };
